import type React from 'react';
import { useEffect, useState } from 'react';
import { listAdminShares, revokeShare, type AdminShare } from '../../lib/adminApi';
import { AdminPageShell, cls } from './shared';

export function SharesPage() {
  const [shares, setShares] = useState<AdminShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load() {
    listAdminShares()
      .then((r) => {
        setShares(r.shares);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleRevoke(share: AdminShare) {
    if (!confirm(`Revoke share ${share.tokenPreview}…? The customer link will stop working.`)) return;
    try {
      await revokeShare(share.id);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Revoke failed');
    }
  }

  if (loading) return <AdminPageShell title="Share Links"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="Share Links"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  const statusClass = (s: AdminShare['status']) =>
    s === 'active'
      ? 'bg-green-100 text-green-700'
      : s === 'expired'
        ? 'bg-gray-100 text-gray-500'
        : 'bg-red-100 text-red-600';

  return (
    <AdminPageShell title="Share Links">
      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>Token</th>
              <th className={cls.th}>Created by</th>
              <th className={cls.th}>Created</th>
              <th className={cls.th}>Expires</th>
              <th className={cls.th}>Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {shares.map((s, i) => (
              <tr key={s.id} className={cls.tr(i % 2 === 1)}>
                <td className={cls.tdMono}>{s.tokenPreview}…</td>
                <td className={cls.td}>
                  {(s.createdByName ?? s.createdByEmail) ? (
                    <div>
                      <div className="text-vari-navy">{s.createdByName ?? s.createdByEmail}</div>
                      {s.createdByName && s.createdByEmail && (
                        <div className="text-xs text-vari-gray">{s.createdByEmail}</div>
                      )}
                    </div>
                  ) : (
                    <span className="font-mono text-xs text-vari-gray">{s.createdBy ?? '—'}</span>
                  )}
                </td>
                <td className={`${cls.td} text-vari-gray`}>
                  {new Date(s.createdAt).toLocaleDateString()}
                </td>
                <td className={`${cls.td} text-vari-gray`}>
                  {s.expiresAt ? new Date(s.expiresAt).toLocaleDateString() : '—'}
                </td>
                <td className={cls.td}>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClass(s.status)}`}>
                    {s.status}
                  </span>
                </td>
                <td className={cls.tdRight}>
                  {s.status === 'active' && (
                    <button
                      onClick={() => void handleRevoke(s)}
                      className={cls.btnDanger}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {shares.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No shares yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminPageShell>
  );
}
